import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { getConversations, getMessages } from "../apis/chat";
import { initSocket, getSocket, disconnectSocket } from "../utils/socket";
import {
  formatTimestampForDisplay,
  getTimestampForSorting,
  normalizeTimestampToISO,
} from "../utils/timestampUtils";
import { usePhoneAccount } from "./PhoneAccountContext";

const LiveChatContext = createContext(null);

/**
 * Format last message text for display
 */
const formatLastMessageText = (lastMessage) => {
  if (!lastMessage) return "No messages";
  
  if (lastMessage.media_url) {
    const mediaType = lastMessage.message_type || "media";
    const mediaTypeLabels = {
      image: "📷 Image",
      video: "🎥 Video",
      audio: "🎵 Audio",
      document: "📄 Document",
      sticker: "🎨 Sticker",
    };
    let text = mediaTypeLabels[mediaType] || "📎 Media";
    if (lastMessage.message_text) {
      text += `: ${lastMessage.message_text}`;
    }
    return text;
  }
  
  if (lastMessage.message_type === "button") {
    if (lastMessage.button?.text) {
      return `🔘 ${lastMessage.button.text}`;
    }
    if (lastMessage.message_text) {
      return `🔘 ${lastMessage.message_text}`;
    }
    return "🔘 Button Interaction";
  }
  
  return lastMessage.message_text || "No messages";
};

/**
 * LiveChat Provider Component
 */
export const LiveChatProvider = ({ children }) => {
  // Get phone account state from PhoneAccountContext
  const { selectedPhoneId, setSelectedPhoneId } = usePhoneAccount();
  
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  
  // Store real-time messages (received via socket but not yet in database)
  // Key: `${customerPhone}_${phoneNumberId}`, Value: array of messages
  const realtimeMessagesRef = useRef(new Map());
  
  // Store current selected chat in ref to avoid closure issues in socket listeners
  const selectedChatRef = useRef(null);
  
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  /**
   * Handle phoneId changes - clear chats when phoneId changes
   */
  useEffect(() => {
    if (selectedPhoneId) {
      // Clear chats when phoneId changes
      setChats([]);
      setSelectedChat(null);
      setMessages([]);
    }
  }, [selectedPhoneId]);

  /**
   * Fetch conversations from API and join all chat rooms
   */
  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getConversations(selectedPhoneId);
      if (response.data.success) {
        const formattedChats = response.data.data.map((chat) => {
          const lastMessageAt = chat.last_message?.sent_at || chat.last_message_at;
          return {
            id: `${chat.customer_phone}_${chat.phone_number_id}`,
            customer_phone: chat.customer_phone,
            phone_number_id: chat.phone_number_id,
            name: chat.customer_name || chat.customer_phone,
            phone: chat.customer_phone,
            lastMessage: formatLastMessageText(chat.last_message),
            timestamp: formatTimestampForDisplay(lastMessageAt),
            unread: chat.unread_count || 0,
            is_window_open: chat.is_window_open !== undefined ? chat.is_window_open : true,
            last_message: chat.last_message || null,
            last_message_at: lastMessageAt,
          };
        });
        
        // Sort by last_message_at descending (newest first)
        formattedChats.sort((a, b) => {
          const timeA = new Date(a.last_message_at || 0).getTime();
          const timeB = new Date(b.last_message_at || 0).getTime();
          return timeB - timeA;
        });
        
        setChats(formattedChats);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedPhoneId, user.id]);

  /**
   * Fetch messages for a conversation
   * Merges database messages with real-time messages (received via socket but not yet in DB)
   */
  const fetchMessages = useCallback(async (customerPhone, phoneNumberId) => {
    try {
      if (!customerPhone || !phoneNumberId) {
        console.error("Missing parameters for fetchMessages:", { customerPhone, phoneNumberId });
        return;
      }
      setMessagesLoading(true);
      
      // Fetch messages from database
      const response = await getMessages(customerPhone, phoneNumberId, 50, 0);
      if (response.data.success) {
        const dbMessages = response.data.data.messages || [];
        // Normalize sent_at for database messages (MySQL returns Date objects or strings in UTC)
        const normalizedDbMessages = dbMessages.map(msg => ({
          ...msg,
          sent_at: normalizeTimestampToISO(msg.sent_at) || msg.sent_at,
        }));
        
        // Get real-time messages for this chat (messages received via socket but not yet in DB)
        const chatKey = `${customerPhone}_${phoneNumberId}`;
        const realtimeMessages = realtimeMessagesRef.current.get(chatKey) || [];
        
        // Normalize sent_at for real-time messages
        const normalizedRealtimeMessages = realtimeMessages.map(msg => ({
          ...msg,
          sent_at: normalizeTimestampToISO(msg.sent_at) || msg.sent_at,
        }));
        
        // Merge database messages with real-time messages
        // Create a map to track messages by message_id to avoid duplicates
        const messagesMap = new Map();
        
        // Add database messages first
        normalizedDbMessages.forEach(msg => {
          if (msg.message_id) {
            messagesMap.set(msg.message_id, msg);
          }
        });
        
        // Add real-time messages (they will overwrite DB messages if duplicate, which is fine)
        normalizedRealtimeMessages.forEach(msg => {
          if (msg.message_id) {
            messagesMap.set(msg.message_id, msg);
          }
        });
        
        // Convert map to array and normalize sent_at to ensure consistent sorting
        const mergedMessages = Array.from(messagesMap.values()).map(msg => ({
          ...msg,
          sent_at: normalizeTimestampToISO(msg.sent_at) || msg.sent_at,
        }));
        
        // Sort by sent_at ascending (oldest first, newest at bottom) using UTC timestamps
        mergedMessages.sort((a, b) => {
          const timeA = getTimestampForSorting(a.sent_at || a.timestamp);
          const timeB = getTimestampForSorting(b.sent_at || b.timestamp);
          
          // Debug log for timing issues
          if (timeA === 0 || timeB === 0) {
            console.warn("Invalid timestamp detected:", {
              msgA: { message_id: a.message_id, sent_at: a.sent_at, timestamp: a.timestamp },
              msgB: { message_id: b.message_id, sent_at: b.sent_at, timestamp: b.timestamp },
            });
          }
          
          return timeA - timeB; // Ascending: oldest first, newest at bottom
        });
        setMessages(mergedMessages);
        
        // Set unread count to zero after fetching messages (messages are marked as read in backend)
        setChats((prevChats) =>
          prevChats.map((c) => {
            if (
              c.customer_phone === customerPhone &&
              c.phone_number_id === phoneNumberId
            ) {
              return { ...c, unread: 0 };
            }
            return c;
          })
        );
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  /**
   * Update conversation in chats list
   */
  const updateConversation = useCallback((messageData) => {
    const customerPhone = messageData.direction === "inbound" 
      ? messageData.from_wa_id 
      : messageData.to_wa_id;
    
    const phoneNumberId = messageData.phone_number_id;
    
    // Only update conversations for the currently selected phoneId
    if (selectedPhoneId && phoneNumberId !== selectedPhoneId) {
      return; // Ignore messages from other phone numbers
    }
    
    const chatId = `${customerPhone}_${phoneNumberId}`;
    
    // Check if this is the selected chat (don't increment unread if it is)
    const isSelectedChat = selectedChat && 
      selectedChat.customer_phone === customerPhone &&
      selectedChat.phone_number_id === phoneNumberId;
    
    setChats((prevChats) => {
      const existingIndex = prevChats.findIndex(
        (chat) => 
          chat.customer_phone === customerPhone && 
          chat.phone_number_id === phoneNumberId
      );

      const lastMessageAt = messageData.sent_at || new Date().toISOString();
      
      // Calculate unread count
      let unreadCount = 0;
      if (existingIndex >= 0) {
        unreadCount = prevChats[existingIndex].unread || 0;
        // Only increment if inbound message and not selected chat
        if (messageData.direction === "inbound" && !isSelectedChat) {
          unreadCount += 1;
        }
      } else {
        // New conversation - only count if inbound
        unreadCount = messageData.direction === "inbound" ? 1 : 0;
      }
      
      // Determine customer name: preserve existing name if message has null customer_name
      let customerName = customerPhone; // Default fallback
      if (existingIndex >= 0) {
        // Existing conversation: preserve existing name if message customer_name is null
        const existingChat = prevChats[existingIndex];
        if (messageData.customer_name) {
          customerName = messageData.customer_name;
        } else {
          // Keep existing name (or fallback to phone if no name was set)
          customerName = existingChat.name || customerPhone;
        }
      } else {
        // New conversation: use customer_name if available, otherwise use phone
        customerName = messageData.customer_name || customerPhone;
      }
      
      const formattedChat = {
        id: chatId,
        customer_phone: customerPhone,
        phone_number_id: phoneNumberId,
        name: customerName,
        phone: customerPhone,
        lastMessage: formatLastMessageText({
          message_text: messageData.message_text,
          message_type: messageData.message_type,
          media_url: messageData.media_url,
        }),
        timestamp: formatTimestampForDisplay(lastMessageAt),
        unread: unreadCount,
        is_window_open: existingIndex >= 0 
          ? prevChats[existingIndex].is_window_open 
          : true,
        last_message: {
          message_text: messageData.message_text,
          message_type: messageData.message_type,
          sent_at: lastMessageAt,
          media_url: messageData.media_url || null,
          mime_type: messageData.mime_type || null,
        },
        last_message_at: lastMessageAt,
      };

      if (existingIndex >= 0) {
        // Update existing conversation - move to top
        const updated = [...prevChats];
        updated.splice(existingIndex, 1);
        return [formattedChat, ...updated];
      } else {
        // Add new conversation at the top
        return [formattedChat, ...prevChats];
      }
    });
  }, [selectedChat, selectedPhoneId]);

  /**
   * Add message to messages state (for currently selected chat)
   * Note: Real-time cache is handled in socket listener
   */
  const addMessage = useCallback((messageData) => {
    const customerPhone = messageData.direction === "inbound" 
      ? messageData.from_wa_id 
      : messageData.to_wa_id;
    
    // Add to current messages state
    setMessages((prev) => {
      // Check if message already exists to prevent duplicates
      const exists = prev.some(msg => 
        msg.message_id === messageData.message_id || 
        (msg.id && msg.id === messageData.id)
      );
      
      if (exists) {
        return prev;
      }

      // Normalize sent_at for consistent sorting using utility
      const newMessage = {
        ...messageData,
        customer_phone: customerPhone,
        sent_at: normalizeTimestampToISO(messageData.sent_at) || messageData.sent_at,
      };
      
      // Add message and sort by sent_at ascending (oldest first, newest at bottom) using UTC timestamps
      const updated = [...prev, newMessage];
      updated.sort((a, b) => {
        const timeA = getTimestampForSorting(a.sent_at || a.timestamp);
        const timeB = getTimestampForSorting(b.sent_at || b.timestamp);
        return timeA - timeB; // Ascending: oldest first, newest at bottom
      });
      
      return updated;
    });
  }, []);

  /**
   * Cleanup: Leave chat room when component unmounts or chat changes
   */
  useEffect(() => {
    return () => {
      const socket = getSocket();
      if (selectedChat && socket && user.id) {
        socket.emit("leave_chat", {
          userId: user.id,
          phoneNumberId: selectedChat.phone_number_id,
          customerPhone: selectedChat.customer_phone,
        });
      }
    };
  }, [selectedChat, user.id]);

  /**
   * Join/Leave user room when phoneId changes
   */
  useEffect(() => {
    const socket = getSocket();
    if (!socket || !user.id || !selectedPhoneId) {
      return;
    }

    // Join user room for current phoneId
    const userRoomId = `user:${user.id}:${selectedPhoneId}`;
    socket.emit("join_user_room", { userId: user.id, phoneId: selectedPhoneId });
    console.log(`👤 Joined user room: ${userRoomId}`);

    // Cleanup: Leave user room when phoneId changes or component unmounts
    return () => {
      if (socket && user.id && selectedPhoneId) {
        socket.emit("leave_user_room", { userId: user.id, phoneId: selectedPhoneId });
        console.log(`👋 Left user room: ${userRoomId}`);
      }
    };
  }, [selectedPhoneId, user.id]);

  /**
   * Initialize socket and set up event listeners
   */
  useEffect(() => {
    const socket = initSocket();

    // Listen for new messages
    socket.on("new_message", (messageData) => {
      const customerPhone = messageData.direction === "inbound" 
        ? messageData.from_wa_id 
        : messageData.to_wa_id;
      
      const messagePhoneId = messageData.phone_number_id;
      
      // Only process messages for the currently selected phoneId
      if (selectedPhoneId && messagePhoneId !== selectedPhoneId) {
        return; // Ignore messages from other phone numbers
      }
      
      // Update conversation list
      updateConversation(messageData);

      // Always store in real-time cache (even if chat not selected)
      // This ensures messages are available when we fetch later
      const phoneNumberId = messagePhoneId;
      const chatKey = `${customerPhone}_${phoneNumberId}`;
      const realtimeMessages = realtimeMessagesRef.current.get(chatKey) || [];
      const messageExists = realtimeMessages.some(msg => msg.message_id === messageData.message_id);
      
      if (!messageExists) {
        // Normalize sent_at before storing
        let normalizedSentAt = messageData.sent_at;
        if (normalizedSentAt) {
          if (normalizedSentAt instanceof Date) {
            normalizedSentAt = normalizedSentAt.toISOString();
          } else if (typeof normalizedSentAt === 'string') {
            const parsed = new Date(normalizedSentAt);
            if (!isNaN(parsed.getTime())) {
              normalizedSentAt = parsed.toISOString();
            }
          }
        }
        
        const messageWithPhone = {
          ...messageData,
          customer_phone: customerPhone,
          sent_at: normalizedSentAt || messageData.sent_at,
        };
        realtimeMessagesRef.current.set(chatKey, [...realtimeMessages, messageWithPhone]);
      }

      // Add message to current messages state if this is the selected chat
      if (selectedChat && 
          customerPhone === selectedChat.customer_phone &&
          phoneNumberId === selectedChat.phone_number_id) {
        addMessage(messageData);
        
        // If this is an inbound message (from customer) and chat is selected, mark as read
        if (messageData.direction === "inbound") {
          // Mark messages as read in backend (async, don't block)
          import("../apis/chat")
            .then(({ markMessagesAsRead }) => {
              return markMessagesAsRead(customerPhone, phoneNumberId);
            })
            .then((response) => {
              if (response.data.success) {
                const markedCount = response.data.data.marked || 0;
                if (markedCount > 0) {
                  console.log(`✅ Marked ${markedCount} messages as read for selected chat`);
                }
                
                // Set unread count to 0 in chats state
                setChats((prevChats) =>
                  prevChats.map((c) => {
                    if (
                      c.customer_phone === customerPhone &&
                      c.phone_number_id === phoneNumberId
                    ) {
                      return { ...c, unread: 0 };
                    }
                    return c;
                  })
                );
              }
            })
            .catch((error) => {
              console.error("Error marking messages as read:", error);
            });
        }
      }
    });


    // Listen for message status updates (only for selected chat)
    socket.on("message_status_update", (statusUpdate) => {
      // Get current selected chat from ref (always has latest value)
      const currentSelectedChat = selectedChatRef.current;
      
      // Only update if this is for the selected chat
      if (currentSelectedChat && 
          statusUpdate.phone_number_id === currentSelectedChat.phone_number_id &&
          statusUpdate.recipient_wa_id === currentSelectedChat.customer_phone) {
        
        // Update message status in messages state
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.message_id === statusUpdate.message_id) {
              
              // Get existing statuses array or create new one
              const existingStatuses = msg.statuses || [];
              
              // Check if this status already exists (avoid duplicates)
              const statusExists = existingStatuses.some(
                (s) => s.status === statusUpdate.status && 
                       s.status_timestamp === statusUpdate.status_timestamp
              );
              
              // Add new status to statuses array if it doesn't exist
              const updatedStatuses = statusExists 
                ? existingStatuses 
                : [
                    ...existingStatuses,
                    {
                      status: statusUpdate.status,
                      status_timestamp: statusUpdate.status_timestamp,
                      error_code: statusUpdate.error_code || null,
                      error_title: statusUpdate.error_title || null,
                      error_message: statusUpdate.error_message || null,
                      error_details: statusUpdate.error_details || null,
                    }
                  ];
              
              return {
                ...msg,
                message_status: statusUpdate.status, // Update current status
                statuses: updatedStatuses, // Update statuses array
                error_code: statusUpdate.error_code || msg.error_code,
                error_title: statusUpdate.error_title || msg.error_title,
                error_message: statusUpdate.error_message || msg.error_message,
                error_details: statusUpdate.error_details || msg.error_details,
                // Update read_at if status is read
                read_at: statusUpdate.status === "read" 
                  ? statusUpdate.status_timestamp 
                  : msg.read_at,
              };
            }
            return msg;
          })
        );
      } else {
        console.log("❌ Status update does not match selected chat:", {
          statusUpdate,
          currentSelectedChat,
          matches: currentSelectedChat && 
            statusUpdate.phone_number_id === currentSelectedChat.phone_number_id &&
            statusUpdate.recipient_wa_id === currentSelectedChat.customer_phone
        });
      }
    });

    // Cleanup on unmount
    return () => {
      if (socket) {
        socket.off("new_message");
        socket.off("message_status_update");
      }
    };
  }, [selectedChat, updateConversation, addMessage, selectedPhoneId]);


  /**
   * Fetch conversations when phone ID changes
   */
  useEffect(() => {
    if (selectedPhoneId) {
      fetchConversations();
    }
  }, [selectedPhoneId, fetchConversations]);

  /**
   * Select chat and fetch messages
   * Also marks messages as read when chat is opened
   */
  const selectChat = useCallback((chat) => {
    const socket = getSocket();
    
    // Leave previous chat room if exists
    if (selectedChat && socket && user.id) {
      socket.emit("leave_chat", {
        userId: user.id,
        phoneNumberId: selectedChat.phone_number_id,
        customerPhone: selectedChat.customer_phone,
      });
    }
    
    setSelectedChat(chat);
    selectedChatRef.current = chat; // Update ref as well
    if (chat) {
      // Fetch messages (marking as read happens automatically in backend when getMessages is called)
      fetchMessages(chat.customer_phone, chat.phone_number_id);
      
      // Join new chat room for status updates
      if (socket && user.id) {
        socket.emit("join_chat", {
          userId: user.id,
          phoneNumberId: chat.phone_number_id,
          customerPhone: chat.customer_phone,
        });
      }
    } else {
      setMessages([]);
    }
  }, [fetchMessages, selectedChat, user.id]);
  
  /**
   * Update a message in messages state
   */
  const updateMessage = useCallback((messageId, updates) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.message_id === messageId ? { ...msg, ...updates } : msg
      )
    );
  }, []);

  const value = {
    // State
    chats,
    selectedChat,
    messages,
    loading,
    messagesLoading,
    selectedPhoneId,
    
    // Actions
    fetchConversations,
    fetchMessages,
    selectChat,
    addMessage,
    updateConversation,
    updateMessage,
  };

  return <LiveChatContext.Provider value={value}>{children}</LiveChatContext.Provider>;
};

/**
 * Hook to use LiveChat context
 */
export const useLiveChat = () => {
  const context = useContext(LiveChatContext);
  if (!context) {
    throw new Error("useLiveChat must be used within LiveChatProvider");
  }
  return context;
};

export default LiveChatContext;

